import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  Badge,
  CircularProgress
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { toggleLikes } from '../../services/api';
import { SnackbarProvider, enqueueSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';
import { getPost } from '../../services/api';

interface PostEngagementProps {
  postId: string;
  initialLikes?: number;
  initialViews?: number;
  setPost: any;
  fetchPosts?: (id: string, incrementViews?: boolean) => Promise<void>;
}

const PostEngagement: React.FC<PostEngagementProps> = ({
  postId,
  initialLikes = 0,
  initialViews = 0,
  setPost,
  fetchPosts
}) => {
  const { user, isAuthenticated } = useAuth();
  const [likes, setLikes] = useState(initialLikes);
  const [views, setViews] = useState(initialViews);
  const [isLiked, setIsLiked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Check if user has liked this post on component mount
  useEffect(() => {
    if (user && isAuthenticated) {
      const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
      setIsLiked(likedPosts.includes(postId));
    }
  }, [user, isAuthenticated, postId]);

  // Function to check current like status from backend
  const checkLikeStatus = async () => {
    if (!user || !isAuthenticated) return;
    
    try {
      const { data } = await getPost(postId, false); // Don't increment views
      if (data.likedBy && Array.isArray(data.likedBy)) {
        const userLiked = data.likedBy.includes(user.id);
        setIsLiked(userLiked);
        
        // Update localStorage
        const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
        if (userLiked && !likedPosts.includes(postId)) {
          likedPosts.push(postId);
        } else if (!userLiked && likedPosts.includes(postId)) {
          const index = likedPosts.indexOf(postId);
          if (index > -1) {
            likedPosts.splice(index, 1);
          }
        }
        localStorage.setItem('likedPosts', JSON.stringify(likedPosts));
      }
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };

  // Check like status when component mounts
  useEffect(() => {
    checkLikeStatus();
  }, [postId, user?.id]);

  // Update local state when props change
  useEffect(() => {
    setLikes(initialLikes);
    setViews(initialViews);
  }, [initialLikes, initialViews]);

  const handleLikeClick = async () => {
    if (!isAuthenticated || !user?.id) {
      enqueueSnackbar('Please login to like posts', { variant: 'info' });
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    
    try {
      // Determine if this is a like or unlike action
      const isLike = isLiked; // true means we're unliking, false means we're liking
      
      const { data } = await toggleLikes(postId, user?.id || '', isLike);
      
      // Update local state
      setLikes(data.likes || 0);
      setIsLiked(data.userLiked || false);
      
      // Update localStorage
      const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
      if (data.userLiked) {
        if (!likedPosts.includes(postId)) {
          likedPosts.push(postId);
        }
      } else {
        const index = likedPosts.indexOf(postId);
        if (index > -1) {
          likedPosts.splice(index, 1);
        }
      }
      localStorage.setItem('likedPosts', JSON.stringify(likedPosts));
      
      // Update parent component
      if (setPost) {
        const updatedPost = { ...data, id: data._id || data.id };
        setPost(updatedPost);
      }
      
      // Refresh like status from backend
      await checkLikeStatus();
      
      // If fetchPosts is provided, use it to update the post data without incrementing views
      if (fetchPosts) {
        await fetchPosts(postId, false);
      }
      
    } catch (error: any) {
      console.error('Error toggling like:', error);
      
      // Handle specific error cases
      if (error.response?.data?.message) {
        enqueueSnackbar(error.response.data.message, { variant: 'error' });
      } else if (error.message) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to update like', { variant: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      {isSubmitting ? (
              <CircularProgress size={24} />
        ) : <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={isLiked ? 'Unlike' : 'Like'}>
          <IconButton
            onClick={handleLikeClick}
            disabled={isSubmitting}
            color={isLiked ? 'primary' : 'default'}
            sx={{ 
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.1)' }
            }}
          >
            { isLiked ? (
              <FavoriteIcon />
            ) : (
              <FavoriteBorderIcon />
            )}
          </IconButton>
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
          {likes}
        </Typography>
      </Box>}

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title="Views">
          <Badge 
            badgeContent={views} 
            color="primary"
            sx={{ 
              '& .MuiBadge-badge': { 
                right: -3,
                top: 3,
              }
            }}
          >
            <VisibilityIcon color="action" />
          </Badge>
        </Tooltip>
      </Box>
      <SnackbarProvider />
    </Box>
  );
};

export default PostEngagement; 